import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';

// Interface pour les transactions périodiques
export interface TransactionPeriodique {
  id?: string;
  description: string;
  montant: number;
  type: 'depense' | 'revenu' | 'transfert';
  categorie_id?: string;
  compte_id: string;
  compte_destination_id?: string;
  date_debut: string; // Format ISO
  frequence: 'quotidienne' | 'hebdomadaire' | 'mensuelle' | 'trimestrielle' | 'annuelle';
  jour_execution?: number;
  mois_execution?: number;
  jour_semaine?: number;
  actif: boolean;
  derniere_execution?: string;
  prochaine_execution?: string;
  nombre_executions?: number;
  nombre_executions_max?: number;
  date_fin?: string;
  user_id?: string;
  /** Chemin Storage (bucket transaction-attachments) — icône liste */
  attachment_path?: string | null;
}

// Interface enrichie pour les transactions périodiques avec détails de jointure
export interface TransactionPeriodiqueWithDetails extends TransactionPeriodique {
  categorie?: {
    id: string;
    nom: string;
    couleur?: string;
    type?: 'depense' | 'revenu' | 'transfert';
  };
  compte?: {
    id: string;
    nom: string;
    couleur?: string;
  };
  compte_destination?: {
    id: string;
    nom: string;
    couleur?: string;
  };
}

// Fonction pour créer une transaction périodique
export const createTransactionPeriodique = async (transaction: TransactionPeriodique) => {
  try {
    // Vérification de la session avant la création
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;
    
    // Ajouter l'ID utilisateur et calculer la prochaine exécution
    const transactionWithUser = { 
      ...transaction, 
      user_id: userId,
      prochaine_execution: calculerProchaineExecution(
        transaction.date_debut,
        transaction.frequence,
        transaction.jour_execution,
        transaction.mois_execution,
        transaction.jour_semaine
      )
    };

    const { data, error } = await supabase
      .from('transactions_periodiques')
      .insert(transactionWithUser)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Exception dans createTransactionPeriodique:', error);
    return { data: null, error };
  }
};

// Fonction pour récupérer une transaction périodique par son ID
export const getTransactionPeriodiqueById = async (id: string) => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;

    const { data, error } = await supabase
      .from('transactions_periodiques')
      .select(`
        *,
        categorie:categories(*),
        compte:comptes!transactions_periodiques_compte_id_fkey(*),
        compte_destination:comptes!transactions_periodiques_compte_destination_id_fkey(*)
      `)
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    return { data, error };
  } catch (error) {
    console.error('Exception dans getTransactionPeriodiqueById:', error);
    return { data: null, error };
  }
};

// Fonction pour récupérer toutes les transactions périodiques
export const getTransactionsPeriodiques = async () => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      console.log('Aucune session active');
      return { data: [], error: null };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;

    console.log('Récupération des transactions périodiques pour l\'utilisateur:', userId);

    let data: any = null;
    let error: any = null;

    if (impersonatedUserId) {
      // Utiliser l'RPC admin pour contourner RLS et récupérer les jointures quand on impersonne
      const rpc = await supabase.rpc('admin_get_transactions_periodiques', { p_user_id: userId });
      if (rpc.error) {
        console.warn('RPC admin_get_transactions_periodiques erreur:', rpc.error);
        error = rpc.error;
      } else {
        data = rpc.data as any[];
      }
    } else {
      const res = await supabase
        .from('transactions_periodiques')
        .select(`
          *,
          categorie:categories(*),
          compte:comptes!transactions_periodiques_compte_id_fkey(*),
          compte_destination:comptes!transactions_periodiques_compte_destination_id_fkey(*)
        `)
        .eq('user_id', userId)
        .order('prochaine_execution', { ascending: true });
      data = res.data;
      error = res.error;
    }

    if (error) {
      console.error('Erreur Supabase:', error);
      // Si la table n'existe pas, retourner un tableau vide
      if (error.code === '42P01') { // Table doesn't exist
        console.log('Table transactions_periodiques n\'existe pas encore');
        return { data: [], error: null };
      }
      return { data: null, error };
    }

    console.log('Données récupérées:', data);
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Exception dans getTransactionsPeriodiques:', error);
    return { data: [], error: null };
  }
};

// Fonction pour mettre à jour une transaction périodique
export const updateTransactionPeriodique = async (id: string, updates: Partial<TransactionPeriodique>) => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;

    // Si des paramètres de fréquence sont modifiés, recalculer la prochaine exécution
    if (updates.frequence || updates.date_debut || 
        updates.jour_execution || updates.mois_execution || 
        updates.jour_semaine) {
      
      // Récupérer la transaction complète
      const { data } = await supabase
        .from('transactions_periodiques')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();
        
      if (data) {
        const completeTransaction = { ...data, ...updates };
        updates.prochaine_execution = calculerProchaineExecution(
          completeTransaction.date_debut,
          completeTransaction.frequence,
          completeTransaction.jour_execution,
          completeTransaction.mois_execution,
          completeTransaction.jour_semaine
        );
      }
    }
    
    const { data, error } = await supabase
      .from('transactions_periodiques')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Exception dans updateTransactionPeriodique:', error);
    return { data: null, error };
  }
};

// Fonction pour supprimer une transaction périodique
export const deleteTransactionPeriodique = async (id: string) => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { success: false, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;

    // Récupérer le chemin d'icône avant suppression
    const { data: existing } = await supabase
      .from('transactions_periodiques')
      .select('attachment_path')
      .eq('id', id)
      .eq('user_id', userId)
      .maybeSingle();

    const { error } = await supabase
      .from('transactions_periodiques')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Erreur dans deleteTransactionPeriodique:', error);
      return { success: false, error };
    }

    if (existing?.attachment_path) {
      try {
        const { removeTransactionAttachmentFile } = await import('./transactionAttachmentService');
        await removeTransactionAttachmentFile(existing.attachment_path);
      } catch (e) {
        console.warn('Icône périodique non supprimée du storage:', e);
      }
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception dans deleteTransactionPeriodique:', error);
    return { success: false, error };
  }
};

// Fonction pour activer/désactiver une transaction périodique
export const toggleTransactionPeriodique = async (id: string, actif: boolean) => {
  try {
    return await updateTransactionPeriodique(id, { actif });
  } catch (error) {
    console.error('Exception dans toggleTransactionPeriodique:', error);
    return { data: null, error };
  }
};

// Fonction pour exécuter manuellement une transaction périodique
export const executeTransactionPeriodique = async (id: string) => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { success: false, error: { message: 'Aucune session active' } };
    }
    const userId = sessionData.session.user.id;

    // Récupérer la transaction périodique
    const { data: transactionPeriodique, error: getError } = await supabase
      .from('transactions_periodiques')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();

    if (getError || !transactionPeriodique) {
      console.error('Erreur lors de la récupération de la transaction périodique:', getError);
      return { success: false, error: getError };
    }

    // Créer la transaction réelle (sans reprendre l'icône périodique comme PJ)
    const transaction = {
      date: new Date().toISOString().split('T')[0],
      categorie_id: transactionPeriodique.categorie_id || '',
      description: transactionPeriodique.description,
      montant: transactionPeriodique.montant,
      compte_id: transactionPeriodique.compte_id,
      compte_destination_id: transactionPeriodique.compte_destination_id,
      type: transactionPeriodique.type,
      user_id: userId,
    };

    // Insérer la transaction
    const { error: insertError } = await supabase
      .from('transactions')
      .insert(transaction);

    if (insertError) {
      console.error('Erreur lors de l\'insertion de la transaction:', insertError);
      return { success: false, error: insertError };
    }

    // Mettre à jour la transaction périodique
    const maintenant = new Date().toISOString();
    const updates: {
      derniere_execution: string;
      nombre_executions: number;
      prochaine_execution: string;
      actif?: boolean;
    } = {
      derniere_execution: maintenant,
      nombre_executions: (transactionPeriodique.nombre_executions || 0) + 1,
      prochaine_execution: calculerProchaineExecution(
        transactionPeriodique.date_debut,
        transactionPeriodique.frequence,
        transactionPeriodique.jour_execution,
        transactionPeriodique.mois_execution,
        transactionPeriodique.jour_semaine,
        new Date(maintenant)
      )
    };

    // Vérifier si on a atteint le nombre maximum d'exécutions
    if (transactionPeriodique.nombre_executions_max && 
        updates.nombre_executions >= transactionPeriodique.nombre_executions_max) {
      updates.actif = false;
    }

    // Vérifier si on a dépassé la date de fin
    if (transactionPeriodique.date_fin && 
        new Date() > new Date(transactionPeriodique.date_fin)) {
      updates.actif = false;
    }

    const { error: updateError } = await supabase
      .from('transactions_periodiques')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId);

    if (updateError) {
      console.error('Erreur lors de la mise à jour de la transaction périodique:', updateError);
      return { success: false, error: updateError };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception dans executeTransactionPeriodique:', error);
    return { success: false, error };
  }
};

// Fonction pour calculer la prochaine date d'exécution
const calculerProchaineExecution = (
  dateDebut: string,
  frequence: string,
  jourExecution?: number,
  moisExecution?: number,
  jourSemaine?: number,
  dateReference?: Date
): string => {
  // Utiliser la date actuelle comme référence si non spécifiée
  const dateRef = dateReference || new Date();
  const prochaine = new Date(dateRef);
  
  // S'assurer que la date de début est passée à un objet Date
  const debut = new Date(dateDebut);
  
  // Si la date de début est dans le futur, c'est la prochaine exécution
  if (debut > dateRef) {
    return debut.toISOString();
  }
  
  switch (frequence) {
    case 'quotidienne':
      // Ajouter un jour à la date de référence
      prochaine.setDate(prochaine.getDate() + 1);
      break;
      
    case 'hebdomadaire':
      if (jourSemaine !== undefined) {
        // Calculer le nombre de jours à ajouter pour atteindre le jour de la semaine souhaité
        const jourActuel = prochaine.getDay(); // 0 = dimanche, 6 = samedi
        let joursAAjouter = jourSemaine - jourActuel;
        if (joursAAjouter <= 0) joursAAjouter += 7; // Si on est déjà passé ce jour cette semaine
        prochaine.setDate(prochaine.getDate() + joursAAjouter);
      } else {
        // Par défaut, ajouter 7 jours
        prochaine.setDate(prochaine.getDate() + 7);
      }
      break;
      
    case 'mensuelle':
      if (jourExecution !== undefined) {
        // Aller au mois suivant
        prochaine.setMonth(prochaine.getMonth() + 1);
        // Définir le jour du mois
        prochaine.setDate(Math.min(jourExecution, new Date(prochaine.getFullYear(), prochaine.getMonth() + 1, 0).getDate()));
      } else {
        // Par défaut, même jour du mois suivant
        prochaine.setMonth(prochaine.getMonth() + 1);
      }
      break;
      
    case 'trimestrielle':
      if (jourExecution !== undefined) {
        // Aller à 3 mois plus tard
        prochaine.setMonth(prochaine.getMonth() + 3);
        // Définir le jour du mois
        prochaine.setDate(Math.min(jourExecution, new Date(prochaine.getFullYear(), prochaine.getMonth() + 1, 0).getDate()));
      } else {
        // Par défaut, même jour 3 mois plus tard
        prochaine.setMonth(prochaine.getMonth() + 3);
      }
      break;
      
    case 'annuelle':
      if (moisExecution !== undefined && jourExecution !== undefined) {
        // Aller à l'année suivante
        prochaine.setFullYear(prochaine.getFullYear() + 1);
        // Définir le mois et le jour
        prochaine.setMonth(moisExecution);
        prochaine.setDate(Math.min(jourExecution, new Date(prochaine.getFullYear(), moisExecution + 1, 0).getDate()));
      } else {
        // Par défaut, même jour et mois de l'année suivante
        prochaine.setFullYear(prochaine.getFullYear() + 1);
      }
      break;
      
    default:
      // Par défaut, ajouter un mois
      prochaine.setMonth(prochaine.getMonth() + 1);
  }
  
  return prochaine.toISOString();
}; 